/**
 * Single source of truth for this project's responsive breakpoints.
 *
 * `tailwind.config.js` derives `theme.screens` from here, and `useBreakpointDown`
 * (`@/composables/useMobile`) builds its media queries from the same numbers — so a JS
 * layout decision can never disagree with a `sm:`/`md:`/`lg:` utility class.
 *
 * Keep this module free of imports. Tailwind loads its config (and therefore this file)
 * through jiti in a plain Node context, outside Vite's module graph: no `@/` alias, no Vue,
 * no CSS. A leaf module with only literals is what keeps that load cheap and reliable.
 *
 * These are NOT Tailwind's stock values. Tailwind's own `md` is 768px; this project has
 * overridden it to 900px since the repo's first commit (0e96fa292, Feb 2020).
 *
 * That matters because a good deal of older code compares against **768** directly —
 * `useMediaQuery("(max-width: 768px)")` calls, hand-written `@media` blocks, and the
 * `maxWidth: 768` default feeding `isMobile` in `@/composables/useMobile`. Those are
 * Tailwind's default leaking into a project that overrode it (768px is also Bootstrap's `md`
 * and the original iPad's portrait width, so it is the number everyone reaches for), not a
 * second, deliberate breakpoint. They flip 132px earlier than any `md:` utility class.
 * They are knowingly left alone; prefer `useBreakpointDown("md")` in new code.
 */
export const BREAKPOINTS = {
  xs: 400,
  sm: 600,
  /** This project's mobile/desktop line. Not Tailwind's 768px default — see the note above. */
  md: 900,
  "1/2lg": 1130,
  lg: 1200,
  "1/2xl": 1400,
  xl: 1500,
  "2xl": 1800,
  "3xl": 2100,
} as const;

/** A breakpoint name from {@link BREAKPOINTS}. */
export type BreakpointName = keyof typeof BREAKPOINTS;

/**
 * {@link BREAKPOINTS} in the `{ md: "900px" }` shape Tailwind's `theme.screens` expects.
 * Consumed by `tailwind.config.js`; a unit test asserts the config still derives from here.
 */
export const TAILWIND_SCREENS: Record<BreakpointName, string> = Object.fromEntries(
  Object.entries(BREAKPOINTS).map(([name, width]) => [name, `${width}px`]),
) as Record<BreakpointName, string>;
